import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Check, RefreshCw, UserPlus, Shield, ExternalLink } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { useAuthStore } from '@/store/authStore'
import { trackEvent } from '@/utils/analytics'
import { formatBytes } from '@/utils/format'
import type { UploadResponse } from '@/types'

interface UploadCompleteProps {
  result: UploadResponse
  onReset: () => void
}

export function UploadComplete({ result, onReset }: UploadCompleteProps) {
  const [copied, setCopied] = useState(false)
  const [copiedFileId, setCopiedFileId] = useState<string | null>(null)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const isEncrypted = result.share_link.includes('#key=')
  const hasMultipleFiles = result.files.length > 1

  // Auto-copy link to clipboard on mount
  useEffect(() => {
    const autoCopy = async () => {
      try {
        await navigator.clipboard.writeText(result.share_link)
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
      } catch (err) {
        // Clipboard API might fail in some browsers
        console.log('Auto-copy failed:', err)
      }
    }
    autoCopy()
  }, [result.share_link])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.share_link)
      trackEvent('share_link_copied')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  const copyFileLink = async (fileId: string, index: number) => {
    try {
      const link = result.share_links?.[index] || `${window.location.origin}/download/${fileId}`
      await navigator.clipboard.writeText(link)
      trackEvent('share_link_copied')
      setCopiedFileId(fileId)
      setTimeout(() => setCopiedFileId(null), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  const getFileLink = (index: number, fileId: string) => {
    return result.share_links?.[index] || `${window.location.origin}/download/${fileId}`
  }

  return (
    <div className="card p-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload Complete!</h2>
        <p className="text-gray-500">
          {result.files.length} file{result.files.length > 1 ? 's' : ''} uploaded successfully
        </p>
        {copied && (
          <p className="text-green-600 text-sm mt-2 font-medium">
            Link copied to clipboard!
          </p>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {hasMultipleFiles ? 'Share Links' : 'Share Link'}
        </label>
        {hasMultipleFiles ? (
          <p className="text-sm text-gray-600 mb-2">
            Each file has its own link — copy them individually below.
          </p>
        ) : (
          <div className="flex">
            <input
              type="text"
              readOnly
              value={result.share_link}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 bg-white border border-gray-300 rounded-l-lg px-4 py-2 text-sm text-gray-900"
            />
            <button
              onClick={copyLink}
              aria-label={copied ? 'Link copied' : 'Copy share link'}
              className="bg-primary-600 text-white px-4 py-2 rounded-r-lg hover:bg-primary-700 transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Expires {new Date(result.expires_at).toLocaleString()}
        </p>
      </div>

      {/* E2E encryption notice */}
      {isEncrypted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">End-to-end encrypted</p>
              <p className="text-xs text-green-700 mt-1">
                This link contains the decryption key. Anyone with this link can decrypt the file. The server never sees the key.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 mb-6">
        {result.files.map((file, index) => (
          <div
            key={file.id}
            className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
          >
            <div className="flex items-center min-w-0">
              <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm text-gray-700 truncate">{file.original_name}</span>
            </div>
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              <span className="text-xs text-gray-400">
                {formatBytes(file.size_bytes)}
              </span>
              <button
                onClick={() => copyFileLink(file.id, index)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded"
                title="Copy link"
                aria-label={`Copy link for ${file.original_name}`}
              >
                {copiedFileId === file.id ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              <a
                href={getFileLink(index, file.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 text-gray-400 hover:text-primary-600 transition-colors rounded"
                title="Open file"
                aria-label={`Open ${file.original_name}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {!isAuthenticated && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <UserPlus className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                Create a free account to track downloads and manage your files.
              </p>
              <Link
                to="/register"
                className="inline-block mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                Sign up free
              </Link>
            </div>
          </div>
        </div>
      )}

      <Button
        variant="secondary"
        className="w-full"
        onClick={onReset}
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Upload more files
      </Button>
    </div>
  )
}
